import { useState } from "react";
import { FileText, FileSpreadsheet, FileArchive, Presentation, Music, Video, Image as ImageIcon, File as FileIcon, Download, ExternalLink, Maximize2, Loader2 } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { extOf, downloadUrl, formatSize } from "@/lib/uploads";
import { PdfPreview, DocViewerDialog, useDocPreview } from "@/components/PdfViewer";

const ICONS = {
  pdf: FileText, doc: FileText, docx: FileText, odt: FileText, rtf: FileText, txt: FileText,
  xls: FileSpreadsheet, xlsx: FileSpreadsheet, csv: FileSpreadsheet, ods: FileSpreadsheet,
  ppt: Presentation, pptx: Presentation, odp: Presentation, key: Presentation,
  zip: FileArchive, rar: FileArchive, "7z": FileArchive, tar: FileArchive, gz: FileArchive,
  mp3: Music, wav: Music, m4a: Music, aac: Music, flac: Music, ogg: Music,
  mp4: Video, mov: Video, webm: Video, avi: Video, mkv: Video,
  jpg: ImageIcon, jpeg: ImageIcon, png: ImageIcon, webp: ImageIcon, gif: ImageIcon, svg: ImageIcon,
};

export function attachmentIcon(nameOrExt = "") {
  const e = nameOrExt.includes(".") ? extOf(nameOrExt) : String(nameOrExt).toLowerCase();
  return ICONS[e] || FileIcon;
}

export default function AttachmentCard({ url, name, size, className = "", testid = "attachment-card", preview = true }) {
  const label = name || "Allegato";
  const ext = extOf(label);
  const Icon = attachmentIcon(ext);
  const href = mediaUrl(url);
  const { previewable: isPdf, status, pdfUrl } = useDocPreview(href, label);
  const showPreview = preview && !!pdfUrl;
  const [viewerOpen, setViewerOpen] = useState(false);
  const openBtnCls = "w-9 h-9 rounded-full border border-white/15 hover:border-gold/60 hover:text-gold flex items-center justify-center transition-colors";
  return (
    <div data-testid={testid} className={`my-3 ${className}`}>
      <div className={`flex items-center gap-3 rounded-xl border border-white/10 bg-[#0B1220] px-3 sm:px-4 py-3 ${showPreview ? "rounded-b-none border-b-0" : ""}`}>
        <div className="w-11 h-11 shrink-0 rounded-lg bg-gold/10 border border-gold/30 flex items-center justify-center text-gold">
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold truncate text-foreground" title={label}>{label}</p>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{ext || "file"}{size ? ` · ${formatSize(size)}` : ""}{status === "checking" ? " · preparazione anteprima…" : isPdf ? " · tocca per sfogliare" : ""}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {isPdf
            ? <button type="button" data-testid={`${testid}-open`} onClick={() => setViewerOpen(true)} title="Apri a schermo intero" className={openBtnCls}><Maximize2 className="w-4 h-4" /></button>
            : <a data-testid={`${testid}-open`} href={href} target="_blank" rel="noopener noreferrer" title="Apri" className={openBtnCls}><ExternalLink className="w-4 h-4" /></a>}
          <a data-testid={`${testid}-download`} href={downloadUrl(href)} download={label} title="Scarica" className="w-9 h-9 rounded-full bg-gold text-[#020617] hover:bg-[#FDE047] flex items-center justify-center transition-colors"><Download className="w-4 h-4" /></a>
        </div>
      </div>
      {showPreview && (
        <div className="rounded-b-xl overflow-hidden border border-white/10 border-t-0 [&>div>button]:rounded-t-none [&>div>button]:border-0">
          <PdfPreview href={pdfUrl} name={label} onOpen={() => setViewerOpen(true)} testid={`${testid}-pdf`} />
        </div>
      )}
      {isPdf && <DocViewerDialog href={href} name={label} pdfUrl={pdfUrl} open={viewerOpen} onClose={() => setViewerOpen(false)} />}
    </div>
  );
}
